import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import { formatDMY } from "@/lib/dateFormat";
import PageHeader from "@/components/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { CreditCard, Info, FileText, Users2, Wallet } from "lucide-react";

export default function MyPayments() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selected, setSelected] = useState(null);
  const [advance, setAdvance] = useState(0);

  useEffect(() => {
    (async () => {
      const [p, pr, e, adv] = await Promise.all([
        api.get("/payments"),
        api.get("/programs"),
        api.get("/enrollments"),
        api.get("/my-advances").catch(() => ({ data: { balance: 0 } })),
      ]);
      setPayments(p.data); setPrograms(pr.data); setEnrollments(e.data);
      setAdvance(Number(adv.data?.balance || 0));
    })();
  }, []);

  const programsMap = useMemo(() => Object.fromEntries(programs.map((p) => [p.id, p.name])), [programs]);
  const enrollmentsMap = useMemo(() => Object.fromEntries(enrollments.map((e) => [e.id, e])), [enrollments]);
  const totalPaid = payments.filter((p) => p.status === "paid").reduce((s, p) => s + Number(p.amount), 0);
  const totalPending = payments.filter((p) => p.status === "pending" && !p.no_charge).reduce((s, p) => s + Number(p.amount), 0);

  const downloadInvoice = async (enrollment_id, payment_id) => {
    // Renamed intent: opens the PDF invoice inline in a new tab. The user can
    // choose to download from their browser's built-in PDF viewer.
    // OJO: hay que mandar `payment_id`. Sin él, el backend devuelve la factura
    // más reciente de la matrícula, así que los ciclos de un mismo programa
    // mostraban TODOS el mismo número y el sello equivocado (una factura por
    // pagar salía sellada "PAGADA"). Reportado con Paul Farley el 10/09/2026.
    try {
      const resp = await api.get(`/enrollments/${enrollment_id}/invoice`, {
        params: { lang: (localStorage.getItem("lang") || "es"), ...(payment_id ? { payment_id } : {}) },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      // silently fail; absence of invoice for very old enrollments is expected
    }
  };

  return (
    <div>
      <PageHeader title={t("my_payments")} testId="my-payments-header" />

      <div className="grid grid-cols-2 gap-3 mb-5 max-w-lg">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">{t("paid")}</div>
          <div className="font-display text-3xl font-bold mt-1 text-green-700">${totalPaid.toFixed(2)}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500">{t("pending")}</div>
          <div className="font-display text-3xl font-bold mt-1 text-orange-600">${totalPending.toFixed(2)}</div>
        </div>
      </div>

      {advance > 0 && (
        <div className="mb-5 flex items-center gap-2.5 rounded-lg border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-900 max-w-lg"
             data-testid="my-advance-balance">
          <Wallet className="h-4 w-4 text-teal-600 shrink-0" />
          <span>
            <strong>${advance.toFixed(2)} {t("advance_balance")}</strong> — {t("advance_balance_hint")}
          </span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>{t("program")}</TableHead>
              <TableHead>{t("amount")}</TableHead>
              <TableHead>{t("classes_progress")}</TableHead>
              <TableHead>{t("status")}</TableHead>
              <TableHead>{t("due_date")}</TableHead>
              <TableHead>Días vencidos</TableHead>
              <TableHead>{t("paid_date")}</TableHead>
              <TableHead className="text-right">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">{t("no_data")}</TableCell></TableRow>
            )}
            {payments.map((p) => {
              // Compartida solo si hay otros estudiantes en la misma factura, o si
              // la pagó alguien distinto al titular (no cuando se paga a sí mismo).
              const isShared = (p.shared_with_student_ids && p.shared_with_student_ids.length > 0)
                || (!!p.paid_by_student_id && p.paid_by_student_id !== p.student_id);
              const isPaid = p.status === "paid";
              const paidByOther = isPaid && p.paid_by_student_id && p.paid_by_student_id !== user.user_id;
              return (
              <TableRow key={p.id} data-testid={`my-pay-row-${p.id}`}>
                <TableCell className="font-medium">
                  <div>{programsMap[p.program_id] || "—"}</div>
                  {isShared && (
                    <div className="mt-1 flex items-center gap-1.5 text-[10px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5 w-fit" data-testid={`shared-badge-${p.id}`}>
                      <Users2 className="h-3 w-3" />
                      Facturación compartida
                    </div>
                  )}
                  {paidByOther && (
                    <div className="mt-1 text-[10px] text-emerald-800" data-testid={`paid-by-${p.id}`}>
                      Pagado por <strong>{p.paid_by_name || "otro estudiante"}</strong>
                    </div>
                  )}
                </TableCell>
                <TableCell>${Number(p.amount).toFixed(2)}</TableCell>
                <TableCell>
                  {p.classes_included > 0 ? (
                    <span className="text-xs font-mono">{p.classes_consumed || 0}/{p.classes_included}</span>
                  ) : <span className="text-slate-400">—</span>}
                </TableCell>
                <TableCell>
                  {p.no_charge ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wide bg-emerald-50 text-emerald-800 border border-emerald-200"
                          data-testid={`no-charge-badge-${p.id}`}>
                      {p.scholarship_covered ? "Becado" : "Sin cobro"}
                    </span>
                  ) : <StatusBadge status={p.status} />}
                </TableCell>
                <TableCell>{formatDMY(p.due_date, lang)}</TableCell>
                <TableCell data-testid={`my-payment-overdue-${p.id}`}>
                  {p.days_overdue > 0 ? (
                    <span className="text-red-600 font-semibold"
                          title={p.status === "paid"
                            ? `Se pagó con ${p.days_overdue} días de atraso`
                            : `Vencida hace ${p.days_overdue} días`}>
                      {p.days_overdue}
                    </span>
                  ) : (p.is_overdue && p.status !== "paid") ? (
                    <span className="text-red-600 font-semibold">Vencida hoy</span>
                  ) : (
                    <span className="text-emerald-600 font-semibold">Al día</span>
                  )}
                </TableCell>
                <TableCell>{formatDMY(p.paid_date, lang)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {p.enrollment_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`invoice-${p.id}`}
                        onClick={() => downloadInvoice(p.enrollment_id, p.id)}
                        title={t("download_invoice")}
                      >
                        <FileText className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {p.status === "pending" && !p.no_charge && (
                      <Button size="sm" data-testid={`pay-now-${p.id}`} onClick={() => setSelected(p)}>
                        <CreditCard className="h-3.5 w-3.5" /> {t("pay_now")}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("pay_now")}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-md">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700">{t("payment_info")}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-500">{t("program")}</div>
                  <div className="font-medium">{programsMap[selected.program_id] || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">{t("amount")}</div>
                  <div className="font-display text-2xl font-bold">${Number(selected.amount).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">{t("due_date")}</div>
                  <div>{formatDMY(selected.due_date, lang)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">{t("status")}</div>
                  <StatusBadge status={selected.status} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {(() => {
              const enr = selected && enrollmentsMap[selected.enrollment_id];
              const externalLink = enr?.payment_link;
              return externalLink ? (
                <>
                  <Button variant="outline" onClick={() => setSelected(null)}>{t("cancel")}</Button>
                  <a
                    href={externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                    data-testid="continue-external-payment-link"
                  >
                    <CreditCard className="h-4 w-4" /> Continuar al pago
                  </a>
                </>
              ) : (
                <Button variant="outline" onClick={() => setSelected(null)}>{t("cancel")}</Button>
              );
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
